/**
 * StatusActionBar.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Barra de ações do agendamento — redesign operacional conforme mockup aprovado.
 *
 * Layout:
 *   1. Status pill compacta (dot + label + dropdown sutil)
 *      (a exclusão do agendamento fica no rodapé do AppointmentDetailPanel)
 *   2. Action toolbar horizontal: Reagendar | Cancelar | Falta | Atender | Faturar
 *      — ícone + label, ghost/neutro, sem caixas coloridas
 *
 * @module StatusActionBar
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { SendWhatsAppMessageToWindow } from '../../../../../api/WhatsappAPI';
import CalendarConfig from '../../../../../consts/CalendarConfig';
import MessagesTemplate from '../../../../../consts/MessagesTemplate';
import { renderAppointmentMessage } from '../../../../../utils/appointmentWhatsAppUtils';
import { useCanSendAgendaWhatsApp } from '../../hooks/useAgendaWhatsAppSend';
import { parseAppointmentDate } from '../../utils/calendarUtils';
import CancelAppointmentDialog from './CancelAppointmentDialog';
import AppointmensState, {
  Scheduled,
  Rescheduled,
  Confirmed,
  ConfirmedByPatient,
  Waiting,
  Doing,
  Done,
  Canceled,
  CanceledByPatient,
  Missed,
  Blocked,
} from '../../../../../consts/AppointmentsState';
import { getStatusColors } from '../../../../../consts/AppointmentStatusColors';
import styles from './AppointmentDetailPanel.module.css';

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const AlertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const PlayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 6 15 12 9 18"/>
  </svg>
);

const InvoiceIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const DotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="5" cy="12" r="2"/>
    <circle cx="12" cy="12" r="2"/>
    <circle cx="19" cy="12" r="2"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

// ── Todos os status disponíveis ───────────────────────────────────────────────

// Status do dropdown que abrem o diálogo de cancelamento em vez de gravar direto
const CANCEL_STATUSES = [Canceled, CanceledByPatient];

// Montado a cada render: as cores seguem as escolhidas pela empresa
const statusOptions = () => [
  { id: Scheduled,          label: 'Agendado',                ...getStatusColors('scheduled') },
  { id: Confirmed,          label: 'Confirmado',              ...getStatusColors('confirmed') },
  { id: ConfirmedByPatient, label: 'Confirmado pelo paciente',...getStatusColors('confirmed_by_patient') },
  { id: Waiting,            label: 'Aguardando no local',     ...getStatusColors('waiting') },
  { id: Doing,              label: 'Em atendimento',          ...getStatusColors('doing') },
  { id: Done,               label: 'Realizado',               ...getStatusColors('completed') },
  { id: Canceled,           label: 'Cancelado',               ...getStatusColors('canceled') },
  { id: CanceledByPatient,  label: 'Cancelado pelo paciente', ...getStatusColors('canceled_by_patient') },
  { id: Missed,             label: 'Falta',                   ...getStatusColors('noshow') },
  { id: Rescheduled,        label: 'Reagendado',              ...getStatusColors('rescheduled') },
];

const CAN_ATTEND = [Scheduled, Confirmed, ConfirmedByPatient, Waiting, Doing, Rescheduled];

function minutesToDuration(minutes) {
  if (typeof minutes === 'string' && minutes.includes(':')) return minutes;
  const mins = Number(minutes) || 50;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
}

function buildUpdatePayload(appointment, newStatus) {
  const date = CalendarConfig.ISOToDate(appointment.date);
  const parsedDate = CalendarConfig.GetAAAAMMDD(date);
  const time = CalendarConfig.GetTime(appointment.date);
  return {
    ...appointment,
    status: newStatus,
    date: parsedDate + ' ' + time,
    patient: appointment.patient,
    resource: appointment.resource,
    procedure: appointment.procedure,
    duration: minutesToDuration(appointment.duration),
    price: appointment.price,
    roomId: appointment.roomId,
  };
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function StatusActionBar({
  appointment,
  onStatusChange,
  onReschedule,
  onDelete,
  onNotifyPatient,
  children,
}) {
  // children: fica ao lado da pill de status (a pill de etapas do atendimento)
  const { UpdateAppointment, DeleteAppointment, GetPatientById, CompanyConfig } = DataProviderInstance();
  const navigate = useNavigate();
  const canSendViaSystem = useCanSendAgendaWhatsApp();

  // ── Resolve nome do paciente e data para o diálogo de cancelamento ──
  const patientObj = appointment?.patient ? GetPatientById(appointment.patient) : null;
  const patientName = patientObj?.name || patientObj?.Name || null;

  let dateStr = null;
  if (appointment?.date) {
    try {
      const d = parseAppointmentDate(appointment.date);
      const day   = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year  = d.getFullYear();
      const h     = String(d.getHours()).padStart(2, '0');
      const m     = String(d.getMinutes()).padStart(2, '0');
      dateStr = `${day}/${month}/${year} às ${h}:${m}`;
    } catch (_) { /* ignora */ }
  }

  const [loadingAction, setLoadingAction] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);
  // Diálogo de cancelamento: status pré-marcado no rádio; null = fechado
  const [cancelDialogStatus, setCancelDialogStatus] = useState(null);
  const statusRef = useRef(null);

  const currentStatus = appointment?.status;
  const allStatusOptions = statusOptions();
  const currentOpt = allStatusOptions.find(o => o.id === currentStatus);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    if (!statusOpen) return;
    const handler = (e) => {
      if (statusRef.current && !statusRef.current.contains(e.target)) {
        setStatusOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [statusOpen]);

  // ── Muda status ───────────────────────────────────────────────────────────

  const changeStatus = useCallback((newStatus, actionKey) => {
    if (loadingAction || !newStatus) return;
    setLoadingAction(actionKey || newStatus);
    setStatusOpen(false);
    const payload = buildUpdatePayload(appointment, newStatus);
    UpdateAppointment(payload, (result) => {
      setLoadingAction(null);
      if (!result?.error) {
        onStatusChange?.(appointment.id, newStatus);
      }
    });
  }, [loadingAction, appointment, UpdateAppointment, onStatusChange]);

  // ── Cancelar (com aviso pelo WhatsApp, feedback #17) ──────────────────────
  // Botão Cancelar e as duas opções "Cancelado" do dropdown passam pelo mesmo
  // diálogo, com o rádio já marcado na opção escolhida.
  const openCancelDialog = (status) => {
    setStatusOpen(false);
    setCancelDialogStatus(status);
  };

  // Aviso só faz sentido com telefone e horário que ainda não passou
  const patientPhone = patientObj?.phone || patientObj?.Phone || patientObj?.cellphone || patientObj?.Cellphone || '';
  const patientCountryCode = String(patientObj?.countryCode || patientObj?.CountryCode || '');
  const canNotifyCancel = !!patientPhone && !!appointment?.date && parseAppointmentDate(appointment.date) > new Date();

  const handleCancelConfirmed = useCallback(({ status, notify }) => {
    if (loadingAction || !status) return;
    const shouldNotify = notify && canNotifyCancel;

    // Sem envio pelo sistema: abre a aba AGORA, dentro do clique (depois do
    // retorno da API o navegador bloqueia o popup); ela recebe o texto após gravar.
    let whatsappWindow = null;
    if (shouldNotify && !canSendViaSystem) {
      whatsappWindow = window.open('', '_blank');
    }

    setLoadingAction('cancel');
    setStatusOpen(false);
    const payload = buildUpdatePayload(appointment, status);
    UpdateAppointment(payload, (result) => {
      setLoadingAction(null);
      if (result?.error) {
        if (whatsappWindow && !whatsappWindow.closed) whatsappWindow.close();
        return;
      }
      setCancelDialogStatus(null);

      // Status gravado primeiro; o aviso vem depois e nunca desfaz o cancelamento
      if (shouldNotify) {
        if (canSendViaSystem) {
          onNotifyPatient?.({ type: 'canceled', appointment_id: appointment.id });
        } else {
          const templates = MessagesTemplate.getTemplates(CompanyConfig);
          const aptDate = parseAppointmentDate(appointment.date);
          const resource = (CompanyConfig?.Resources || []).find(r => String(r.Id) === String(appointment.resource));
          const rawMessage = renderAppointmentMessage(templates.AppointmentCanceledMessage || '', {
            patientName: patientName || '',
            aptDate,
            time: `${String(aptDate.getHours()).padStart(2, '0')}:${String(aptDate.getMinutes()).padStart(2, '0')}`,
            doctorName: resource?.Label || '',
            price: appointment.price,
            companyConfig: CompanyConfig,
          });
          // Modelo não configurado: abre o WhatsApp sem texto, como o fluxo de criação
          const message = rawMessage ? MessagesTemplate.ConvertToWhatsAppText(rawMessage) : '';
          SendWhatsAppMessageToWindow(whatsappWindow, patientPhone, message, patientCountryCode);
        }
      }

      onStatusChange?.(appointment.id, status);
    });
  }, [
    loadingAction, canNotifyCancel, canSendViaSystem, appointment, UpdateAppointment,
    onNotifyPatient, CompanyConfig, patientName, patientPhone, patientCountryCode, onStatusChange,
  ]);

  // ── Excluir bloqueio ──────────────────────────────────────────────────────

  const handleDeleteBlock = useCallback(() => {
    if (loadingAction) return;
    setLoadingAction('delete');
    DeleteAppointment(appointment.id, () => {
      setLoadingAction(null);
      onDelete?.(appointment.id);
    });
  }, [loadingAction, appointment, DeleteAppointment, onDelete]);

  // ── Atender ───────────────────────────────────────────────────────────────

  // Só navega: a tela de atendimento é quem muda o status para "em atendimento",
  // depois que o profissional escolhe o tipo de atendimento.
  const handleAttend = useCallback(() => {
    if (loadingAction) return;
    const appointmentId = appointment?.id;
    if (!appointmentId) return;
    navigate(`/appointment/${appointmentId}`);
  }, [loadingAction, appointment, navigate]);

  // ── Caso especial: bloqueio ───────────────────────────────────────────────

  if (currentStatus === Blocked) {
    return (
      <div className={styles.actionBarWrapper}>
        <button
          type="button"
          className={styles.deleteBlockBtn}
          onClick={handleDeleteBlock}
          disabled={!!loadingAction}
        >
          {loadingAction === 'delete'
            ? <span className={styles.spinner} />
            : <TrashIcon />
          }
          <span>Excluir bloqueio</span>
        </button>
      </div>
    );
  }

  if (!currentStatus || currentStatus === 'available') return null;

  // ── Visibilidade das ações ────────────────────────────────────────────────

  const showConfirm         = [Scheduled, ConfirmedByPatient, Rescheduled].includes(currentStatus);
  const showReschedule      = ![Done].includes(currentStatus);
  const showCancel          = ![Done, Canceled, CanceledByPatient, Missed].includes(currentStatus);
  const showMissed          = [Scheduled, Confirmed, ConfirmedByPatient, Waiting, Rescheduled].includes(currentStatus);
  const showAttend          = CAN_ATTEND.includes(currentStatus);
  const showViewAppointment = true;

  return (
    <div className={styles.actionBarWrapper}>

      {/* ── Dialog de cancelamento (quem cancelou + avisar pelo WhatsApp) ── */}
      <CancelAppointmentDialog
        isOpen={cancelDialogStatus !== null}
        initialStatus={cancelDialogStatus || CanceledByPatient}
        patientName={patientName}
        dateLabel={dateStr}
        canNotify={canNotifyCancel}
        notifyViaSystem={canSendViaSystem}
        loading={loadingAction === 'cancel'}
        onConfirm={handleCancelConfirmed}
        onCancel={() => { if (loadingAction !== 'cancel') setCancelDialogStatus(null); }}
      />


      {/* ── 1. Status pill compacta ─────────────────────────────────────── */}
      <div className={styles.statusSection}>
        <span className={styles.statusSectionLabel}>Status do agendamento</span>
        <div className={styles.statusRow}>

          {/* Pill de status com dropdown */}
          <div className={styles.statusPillWrapper} ref={statusRef}>
            <button
              type="button"
              className={styles.statusPill}
              style={{
                background: currentOpt?.bg || '#F7F8FA',
                color: currentOpt?.textColor || '#374151',
              }}
              onClick={() => setStatusOpen(v => !v)}
              disabled={!!loadingAction}
            >
              <span
                className={styles.statusDot}
                style={{ background: currentOpt?.borderColor || '#9ca3af' }}
              />
              <span className={styles.statusPillLabel}>{currentOpt?.label || currentStatus}</span>
              {loadingAction && allStatusOptions.some(o => o.id === loadingAction)
                ? <span className={styles.spinner} style={{ width: 12, height: 12 }} />
                : <ChevronDownIcon />
              }
            </button>

            {/* Dropdown de status */}
            {statusOpen && (
              <div className={styles.statusDropdown}>
                <div className={styles.statusDropdownTitle}>Alterar status</div>
                {allStatusOptions.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    className={styles.statusDropdownItem}
                    style={{
                      background: opt.bg,
                      color: opt.textColor,
                    }}
                    onClick={() => (CANCEL_STATUSES.includes(opt.id) ? openCancelDialog(opt.id) : changeStatus(opt.id, opt.id))}
                    disabled={opt.id === currentStatus}
                  >
                    <span
                      className={styles.statusDot}
                      style={{ background: opt.borderColor }}
                    />
                    <span>{opt.label}</span>
                    {opt.id === currentStatus && (
                      <span className={styles.statusDropdownCheck}><CheckIcon /></span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {children}
        </div>
      </div>

      {/* ── 2. Action toolbar horizontal ────────────────────────────────── */}
      <div className={styles.actionToolbar}>

        {showConfirm && (
          <button
            type="button"
            className={`${styles.toolbarBtn} ${styles.toolbarBtnConfirm}`}
            onClick={() => changeStatus(Confirmed, 'confirm')}
            disabled={!!loadingAction}
            title="Confirmar agendamento"
          >
            {loadingAction === 'confirm'
              ? <span className={styles.spinner} />
              : <CheckIcon />
            }
            <span>Confirmar</span>
          </button>
        )}

        {showReschedule && (
          <button
            type="button"
            className={styles.toolbarBtn}
            onClick={() => onReschedule?.(appointment)}
            disabled={!!loadingAction}
            title="Reagendar"
          >
            <CalendarIcon />
            <span>Reagendar</span>
          </button>
        )}

        {showCancel && (
          <button
            type="button"
            className={`${styles.toolbarBtn} ${styles.toolbarBtnCancel}`}
            onClick={() => openCancelDialog(CanceledByPatient)}
            disabled={!!loadingAction}
            title="Cancelar"
          >
            {loadingAction === 'cancel'
              ? <span className={styles.spinner} />
              : <XIcon />
            }
            <span>Cancelar</span>
          </button>
        )}

        {showMissed && (
          <button
            type="button"
            className={`${styles.toolbarBtn} ${styles.toolbarBtnMissed}`}
            onClick={() => changeStatus(Missed, 'missed')}
            disabled={!!loadingAction}
            title="Registrar falta"
          >
            {loadingAction === 'missed'
              ? <span className={styles.spinner} />
              : <AlertIcon />
            }
            <span>Falta</span>
          </button>
        )}

        {showViewAppointment && (
          <button
            type="button"
            className={`${styles.toolbarBtn} ${styles.toolbarBtnView}`}
            onClick={() => navigate(`/appointment/${appointment.id}`)}
            disabled={!!loadingAction}
            title="Ver prontuário"
          >
            <ExternalLinkIcon />
            <span>Ver prontuário</span>
          </button>
        )}

        {showAttend && (
          <button
            type="button"
            className={`${styles.toolbarBtn} ${styles.toolbarBtnAttend}`}
            onClick={handleAttend}
            disabled={!!loadingAction}
            data-tour="appointment-attend"
            title={currentStatus === Doing ? 'Ir para atendimento' : 'Atender'}
          >
            {loadingAction === 'attend'
              ? <span className={styles.spinner} />
              : <PlayIcon />
            }
            <span>{currentStatus === Doing ? 'Atender' : 'Atender'}</span>
          </button>
        )}

      </div>

    </div>
  );
}
